import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Upload, Loader2, Star, Trash2, Video, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import apiClient, { propertyApi } from "@/utils/api";
import PropertyBasicInfo from "@/components/property/PropertyBasicInfo";
import PropertyLocation from "@/components/property/PropertyLocation";
import PropertyDetails from "@/components/property/PropertyDetails";
import PropertyFeatures from "@/components/property/PropertyFeatures";
import PropertyViewsChart from "@/components/admin/PropertyViewsChart";

const PropertyFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const docInputRef = useRef(null);
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Basic Info
  const [name, setName] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [priceUsd, setPriceUsd] = useState("");
  const [priceRent, setPriceRent] = useState("");
  const [areaM2, setAreaM2] = useState("");
  
  // Location
  const [department, setDepartment] = useState("");
  const [zone, setZone] = useState("");
  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  
  // Details
  const [bedrooms, setBedrooms] = useState(0);
  const [bathrooms, setBathrooms] = useState(0);
  const [parkingSpaces, setParkingSpaces] = useState(0);
  const [description, setDescription] = useState("");
  
  // Features & Media
  const [features, setFeatures] = useState([]);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isFeatured, setIsFeatured] = useState(false);

  useEffect(() => {
    if (isEditing) {
      fetchProperty();
    }
  }, [id, isEditing]);

  const fetchProperty = async () => {
    setLoading(true);
    try {
      const response = await propertyApi.getById(id);
      const p = response.data;
      setName(p.name || "");
      setPropertyType(p.property_type || "");
      setTransactionType(p.transaction_type || "");
      setPriceUsd(p.price_usd || "");
      setAreaM2(p.area_m2 || "");
      setDepartment(p.department || "");
      setZone(p.zone || "");
      setAddress(p.address || "");
      setBedrooms(p.bedrooms || 0);
      setBathrooms(p.bathrooms || 0);
      setParkingSpaces(p.parking_spaces || 0);
      setDescription(p.description || "");
      setFeatures(p.features || []);
      setImages(p.images || []);
      setVideos(p.videos || []);
      setDocuments(p.documents || []);
      setIsFeatured(p.is_featured || false);
      setLatitude(p.latitude || "");
      setLongitude(p.longitude || "");
    } catch (error) {
      toast.error("Error al cargar la propiedad");
      navigate("/admin/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    const newImages = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        // Try Drive upload first (falls back to MongoDB if not connected)
        const response = await apiClient.post('/drive/upload/image', formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        newImages.push({ 
          url: response.data.url, 
          alt: file.name, 
          is_main: images.length === 0 && newImages.length === 0,
          storage: response.data.storage || 'unknown'
        });
      } catch (error) {
        toast.error(`Error al subir ${file.name}`);
      }
    }
    setImages([...images, ...newImages]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploadingVideo(true);

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        // Try Drive upload first (falls back to MongoDB if not connected)
        const response = await apiClient.post('/drive/upload/video', formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        setVideos(prev => [...prev, { url: response.data.url, title: file.name }]);
      } catch (error) {
        toast.error(`Error al subir ${file.name}`);
      }
    }
    setUploadingVideo(false);
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploadingDoc(true);

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        // Try Drive upload first (falls back to MongoDB if not connected)
        const response = await apiClient.post('/drive/upload/document', formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        setDocuments(prev => [...prev, { url: response.data.url, title: file.name, filename: response.data.filename }]);
      } catch (error) {
        toast.error(`Error al subir ${file.name}`);
      }
    }
    setUploadingDoc(false);
    if (docInputRef.current) docInputRef.current.value = "";
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    if (images[index]?.is_main && newImages.length > 0) {
      newImages[0].is_main = true;
    }
    setImages(newImages);
  };

  const handleSetMainImage = (index) => {
    setImages(images.map((img, i) => ({ ...img, is_main: i === index })));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !propertyType || !transactionType || !priceUsd || !areaM2 || !department) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }
    
    // Validate rent price for "Venta y Renta"
    if (transactionType === "Venta y Renta" && !priceRent) {
      toast.error("Por favor ingresa el precio de renta");
      return;
    }

    setSaving(true);
    try {
      const basePayload = {
        name, 
        property_type: propertyType, 
        area_m2: parseFloat(areaM2),
        department, zone, address,
        latitude: latitude ? parseFloat(latitude) : null,
        longitude: longitude ? parseFloat(longitude) : null,
        bedrooms: parseInt(bedrooms) || 0, 
        bathrooms: parseInt(bathrooms) || 0,
        parking_spaces: parseInt(parkingSpaces) || 0,
        description, features, images, videos, documents, 
        is_featured: isFeatured
      };

      if (isEditing) {
        // When editing, just update normally
        const payload = {
          ...basePayload,
          transaction_type: transactionType === "Venta y Renta" ? "Venta" : transactionType,
          price_usd: parseFloat(priceUsd)
        };
        await propertyApi.update(id, payload);
        toast.success("Propiedad actualizada exitosamente");
      } else {
        // When creating new
        if (transactionType === "Venta y Renta") {
          // Use the atomic dual-creation endpoint
          const dualPayload = {
            ...basePayload,
            price_sale: parseFloat(priceUsd),
            price_rent: parseFloat(priceRent)
          };
          
          const response = await propertyApi.createDual(dualPayload);
          toast.success(response.data.message || "Se crearon 2 propiedades: una para Venta y otra para Alquiler");
        } else {
          // Single property
          const payload = {
            ...basePayload,
            transaction_type: transactionType,
            price_usd: parseFloat(priceUsd)
          };
          await propertyApi.create(payload);
          toast.success("Propiedad creada exitosamente");
        }
      }
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Error al guardar la propiedad");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin/dashboard" className="text-[#A3A3A3] hover:text-white transition-colors">
            <ArrowLeft size={24} />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">
              {isEditing ? "Editar Propiedad" : "Nueva Propiedad"}
            </h1>
            <p className="text-[#A3A3A3]">
              {isEditing ? "Modifica los datos de la propiedad" : "Completa la información de la nueva propiedad"}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* View Statistics - Only when editing */}
          {isEditing && (
            <PropertyViewsChart propertyId={id} />
          )}

          {/* Basic Info */}
          <PropertyBasicInfo
            name={name} setName={setName}
            propertyType={propertyType} setPropertyType={setPropertyType}
            transactionType={transactionType} setTransactionType={setTransactionType}
            priceUsd={priceUsd} setPriceUsd={setPriceUsd}
            priceRent={priceRent} setPriceRent={setPriceRent}
            areaM2={areaM2} setAreaM2={setAreaM2}
          />

          {/* Location */}
          <PropertyLocation
            department={department} setDepartment={setDepartment}
            zone={zone} setZone={setZone}
            address={address} setAddress={setAddress}
            latitude={latitude} setLatitude={setLatitude}
            longitude={longitude} setLongitude={setLongitude}
          />

          {/* Details */}
          <PropertyDetails
            bedrooms={bedrooms} setBedrooms={setBedrooms}
            bathrooms={bathrooms} setBathrooms={setBathrooms}
            parkingSpaces={parkingSpaces} setParkingSpaces={setParkingSpaces}
            description={description} setDescription={setDescription}
            name={name}
            propertyType={propertyType}
            transactionType={transactionType}
            priceUsd={priceUsd}
            areaM2={areaM2}
            zone={zone}
            department={department}
            features={features}
          />

          {/* Features */}
          <PropertyFeatures features={features} setFeatures={setFeatures} />

          {/* Images */}
          <section className="bg-[#121212] border border-white/5 p-6 space-y-6">
            <h2 className="text-lg text-white font-medium">Imágenes</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {images.map((img, idx) => (
                <div key={`img-${idx}`} className="relative group aspect-square bg-black/40 rounded-lg overflow-hidden">
                  <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button type="button" onClick={() => handleSetMainImage(idx)} className={`p-2 rounded-full ${img.is_main ? "bg-[#D4AF37] text-black" : "bg-white/20 text-white hover:bg-white/30"}`}>
                      <Star size={16} />
                    </button>
                    <button type="button" onClick={() => handleRemoveImage(idx)} className="p-2 rounded-full bg-red-500/80 text-white hover:bg-red-500">
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {img.is_main && <span className="absolute top-2 left-2 bg-[#D4AF37] text-black text-xs px-2 py-0.5 rounded">Principal</span>}
                </div>
              ))}
              <label className="aspect-square bg-black/40 border-2 border-dashed border-white/10 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
                {uploading ? <Loader2 className="animate-spin text-[#D4AF37]" size={24} /> : (
                  <>
                    <Upload size={24} className="text-[#A3A3A3] mb-2" />
                    <span className="text-[#A3A3A3] text-sm">Subir imágenes</span>
                  </>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
              </label>
            </div>
          </section>

          {/* Videos */}
          <section className="bg-[#121212] border border-white/5 p-6 space-y-6">
            <h2 className="text-lg text-white font-medium flex items-center gap-2">
              <Video size={20} className="text-[#D4AF37]" /> Videos
            </h2>
            <div className="space-y-3">
              {videos.map((video, idx) => (
                <div key={`video-${idx}`} className="flex items-center gap-3 p-3 bg-black/40 rounded-lg">
                  <Video size={20} className="text-[#D4AF37]" />
                  <span className="text-white flex-1 truncate">{video.title}</span>
                  <button type="button" onClick={() => setVideos(videos.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-500">
                    <X size={18} />
                  </button>
                </div>
              ))}
              <label className="flex items-center justify-center gap-2 p-4 bg-black/40 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
                {uploadingVideo ? <Loader2 className="animate-spin text-[#D4AF37]" size={20} /> : (
                  <>
                    <Upload size={20} className="text-[#A3A3A3]" />
                    <span className="text-[#A3A3A3]">Subir video</span>
                  </>
                )}
                <input ref={videoInputRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
              </label>
            </div>
          </section>

          {/* Documents */}
          <section className="bg-[#121212] border border-white/5 p-6 space-y-6">
            <h2 className="text-lg text-white font-medium flex items-center gap-2">
              <FileText size={20} className="text-[#D4AF37]" /> Documentos PDF
            </h2>
            <div className="space-y-3">
              {documents.map((doc, idx) => (
                <div key={`doc-${idx}`} className="flex items-center gap-3 p-3 bg-black/40 rounded-lg">
                  <FileText size={20} className="text-[#D4AF37]" />
                  <span className="text-white flex-1 truncate">{doc.title}</span>
                  <button type="button" onClick={() => setDocuments(documents.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-500">
                    <X size={18} />
                  </button>
                </div>
              ))}
              <label className="flex items-center justify-center gap-2 p-4 bg-black/40 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
                {uploadingDoc ? <Loader2 className="animate-spin text-[#D4AF37]" size={20} /> : (
                  <>
                    <Upload size={20} className="text-[#A3A3A3]" />
                    <span className="text-[#A3A3A3]">Subir PDF</span>
                  </>
                )}
                <input ref={docInputRef} type="file" accept=".pdf" onChange={handleDocUpload} className="hidden" />
              </label>
            </div>
          </section>

          {/* Featured & Submit */}
          <section className="bg-[#121212] border border-white/5 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-medium">Propiedad Destacada</h3>
                <p className="text-[#A3A3A3] text-sm">Las propiedades destacadas aparecen primero en el listado</p>
              </div>
              <Switch checked={isFeatured} onCheckedChange={setIsFeatured} />
            </div>
          </section>

          {/* Submit buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button type="submit" disabled={saving} className="flex-1 h-12 bg-[#D4AF37] text-black hover:bg-[#FFE082] font-semibold">
              {saving ? <Loader2 className="animate-spin mr-2" size={20} /> : null}
              {isEditing ? "Guardar Cambios" : "Crear Propiedad"}
            </Button>
            <Link to="/admin/dashboard" className="flex-1">
              <Button type="button" variant="outline" className="w-full h-12 border-white/10 text-white">
                Cancelar
              </Button>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyFormPage;
